/**
 * Klanten Store — customer search + detail state management
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useRecentCustomersStore } from './recent-customers-store';

// ─── TYPES ──────────────────────────────────────────────────────

export interface BrandFlags {
  cinco: boolean;
  kleda: boolean;
  sofasaver: boolean;
  schoonhuisshop: boolean;
}

export interface CustomerResult {
  id: string;
  type: 'account' | 'lead' | 'account_business' | 'account_person';
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  // Verrijkt door /api/customers/search (alleen voor account-resultaten).
  // Optioneel zodat lead-resultaten en recent-created-items geen dummy-waarden nodig hebben.
  orderCount?: number;
  totalSpend?: number;
  lastOrderDate?: string | null;
  openInvoiceAmount?: number;
  /** Account staat op de blacklist (geen zaken meer doen). */
  blacklisted?: boolean;
  blacklistReden?: string | null;
  brandFlags?: BrandFlags;
}

export interface CustomerCore extends CustomerResult {
  firstName: string;
  lastName: string;
  mobile: string;
  street: string;
  country: string;
  billingAddressToevoeging: string;
  shippingStreet: string;
  shippingPostcode: string;
  shippingCity: string;
  shippingCountry: string;
  shippingAddressToevoeging: string;
  createdDate: string;
  lastModifiedDate: string;
  isPersonAccount?: boolean;
  mutatieBron: string;
  /** Marketing-opt-out. Lead/PersonAccount: het opt-out-veld van de klant zelf. */
  emailOptOut?: boolean;
  /** Account staat op de blacklist (geen zaken meer doen). Leads altijd false. */
  blacklisted?: boolean;
  /** Reden bij blacklist (Account.Blacklist_Reden__c), null indien leeg. */
  blacklistReden?: string | null;
  /** Klant betaalt altijd op rekening (Account.Betalingswijze__c = 'Factuur'): nieuwe orders krijgen Op_Factuur automatisch. */
  betaaltOpFactuur?: boolean;
  betalingswijze?: string | null;
  betalingstermijn?: string | null;
  brandFlags: BrandFlags;
}

/** Uitkomst van de fan-out naar lopende orders als de klant op 'Factuur' gaat. */
export interface OpFactuurOrdersUitkomst {
  gezet: Array<{ sfId: string; naam: string | null }>;
  overgeslagen: Array<{ sfId: string; naam: string | null; reden: string }>;
}

/**
 * Patch-shape die KlantgegevensCard doorgeeft. Alle velden optioneel; velden
 * die in de call zitten worden door de server (AccountMutationService) naar
 * SF-API-namen vertaald. Alleen flat fields — brandFlags wordt afzonderlijk
 * doorgegeven omdat de UI per checkbox 1 veld patcht.
 */
export type AccountPatch = Partial<Omit<CustomerCore, 'id' | 'type' | 'createdDate' | 'lastModifiedDate' | 'isPersonAccount' | 'mutatieBron' | 'brandFlags' | 'betalingswijze' | 'betalingstermijn'>> & {
  brandFlags?: Partial<BrandFlags>;
};

export type CreateCustomerInput =
  | {
      type: 'Person';
      firstName?: string;
      lastName: string;
      email?: string;
      phone?: string;
      mobile?: string;
      street?: string;
      postcode?: string;
      city?: string;
      country?: string;
      skipDuplicateCheck?: boolean;
    }
  | {
      type: 'Business';
      companyName: string;
      // Contact (primaire contactpersoon)
      firstName?: string;
      lastName: string;
      email?: string;
      phone?: string;       // contact-phone
      mobile?: string;      // contact-mobile
      // Account-niveau
      accountPhone?: string;
      street?: string;
      postcode?: string;
      city?: string;
      country?: string;
      skipDuplicateCheck?: boolean;
    };

export interface DuplicateCustomerMatch {
  sobjectId: string;
  sobjectType: 'Account' | 'Lead';
  name: string;
  email?: string | null;
  phone?: string | null;
  signal: 'email' | 'phone' | 'phone_and_email';
}

export type CreateCustomerResult =
  | { ok: true; accountId: string; contactId: string | null }
  | { ok: false; error: string; code?: string; duplicates?: DuplicateCustomerMatch[] };

export interface ContactInfo {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  department: string;
  emailOptOut?: boolean;
}

/**
 * Eén event in de uniforme klant-communicatietijdlijn. Server-side genormaliseerd
 * (api/_lib/customer-timeline.ts) uit alle bronnen: inbox, SF-mails, proactieve
 * notificaties, telefonie en marketing. `contentKind` stuurt hoe de drawer de
 * inhoud rendert; `contentRef`/`body` leveren die inhoud (lazy resp. inline).
 */
export interface TimelineItem {
  id: string;
  /** Bron van het event. */
  source: 'inbox' | 'sf_email' | 'eigen_mail' | 'notification' | 'call' | 'marketing';
  /** Inbox-conversatie om naar door te linken; null voor overige bronnen. */
  conversationId: string | null;
  channel: 'email' | 'whatsapp' | 'phone' | 'sms';
  isIncoming: boolean;
  fromName: string;
  subject: string;
  snippet: string;
  date: string;
  brand: string;
  /** sent | suppressed | failed (notify) of call/marketing-status; stuurt kleur. */
  status?: string;
  /** Hoe de drawer de inhoud rendert. */
  contentKind: 'sf_email' | 'text' | 'message' | 'call' | 'marketing';
  /** Sleutel om de volledige inhoud lazy te laden (EmailMessageId / callId / sendId). */
  contentRef?: string | null;
  /** Inline inhoud waar die kort is (SMS-tekst, notificatie-text, inbox body_text). */
  body?: string | null;
}

export interface OrderInfo {
  id: string;
  reference: string;
  status: string;
  brand: string;
  total: number;
  openAmount: number;
  paymentMethod: string;
  paymentReceived: boolean;
  pickupDate: string | null;
  deliveryDate: string | null;
  createdDate: string;
  /** B2B: Werk_Order__c.Contactpersoon__c (+ geresolvde naam). */
  contactId?: string | null;
  contactName?: string | null;
}

export interface OpportunityInfo {
  id: string;
  name: string;
  stage: string;
  amount: number;
  brand: string;
  createdDate: string;
  closeDate: string;
  /** Cinco-platform doorverwijs-velden (alleen gevuld voor Brand__c='CINCO'). */
  opportunityNumber?: string;
  /** Opportunity__c — type/soort, bv. "Offerte Extern". */
  offerType?: string;
  channel?: string;
  segment?: string;
  webSource?: string;
  /** Partner waar de aanvraag naartoe is doorverwezen (Partner__r.Name). */
  partnerId?: string | null;
  partnerName?: string;
  /** Plaats van de doorverwezen klus (Doorverwezen_Plaats__c). */
  doorverwezenPlaats?: string;
  commissionAmount?: number;
  totalInclBtw?: number;
}

export interface InvoiceInfo {
  id: string;
  name: string;
  date: string;
  total: number;
  openAmount: number;
  transactionTotal: number;
  paid: boolean;
  paymentMethod: string;
  workOrderRef: string;
}

export interface EmailMessageInfo {
  id: string;
  subject: string;
  messageDate: string;
  fromAddress: string;
  fromName: string;
  toAddress: string;
  ccAddress: string | null;
  status: number;
  hasAttachment: boolean;
  isIncoming: boolean;
  relatedToId: string;
  workOrderRef: string | null;
}

/** Offerte (Lead) van deze klant — mirror-read via /api/customers/detail. */
export interface LeadOfferteInfo {
  id: string;
  leadnummer: string;
  name: string;
  status: string;
  brand: string;
  total: number;
  createdDate: string;
  isConverted: boolean;
  convertedDate: string | null;
  offerteVerstuurd: string | null;
  vervaldatum: string | null;
  /**
   * 'account' = de lead wijst zelf naar deze klant.
   * 'vangnet' = alleen e-mail of telefoon matcht, dus de koppeling is een gok.
   */
  koppeling: 'account' | 'vangnet';
}

export interface ConversationInfo {
  id: string;
  subject: string;
  snippet: string;
  channel: 'email' | 'whatsapp';
  brand: string;
  status: string;
  fromName: string;
  fromEmail: string;
  phone: string;
  lastMessageAt: string;
  messageCount: number;
}

export interface CustomerDetail {
  customer: CustomerCore;
  orders: OrderInfo[];
  opportunities: OpportunityInfo[];
  invoices: InvoiceInfo[];
  conversations: ConversationInfo[];
  timeline: TimelineItem[];
  contacts?: ContactInfo[];
  werkaanvragen?: OrderInfo[];
  emails?: EmailMessageInfo[];
  leads?: LeadOfferteInfo[];
}

// ─── MERGE (klanten samenvoegen) ────────────────────────────────

export interface MergeChildCounts {
  orders: number;
  invoices: number;
  cases: number;
  opportunities: number;
}

/** Canoniek (billing) adres met SF-field-namen als keys → mapt 1-op-1 op fieldOverrides. */
export interface MergeAddressFields {
  BillingStreet: string;
  BillingPostalCode: string;
  BillingCity: string;
  BillingCountry: string;
  BillingAddress_toevoeging__c: string;
}

export interface MergeCandidate {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  createdDate: string;
  isPersonAccount: boolean;
  blacklisted: boolean;
  blacklistReden: string | null;
  /** Welk signaal leidde tot de match (phone/email/beide). */
  signal: 'phone' | 'email' | 'phone_and_email' | null;
  counts: MergeChildCounts;
  address: MergeAddressFields;
}

export interface MergeSource {
  id: string;
  name: string;
  createdDate: string;
  counts: MergeChildCounts;
  address: MergeAddressFields;
}

export interface MergeCandidatesResult {
  source: MergeSource;
  candidates: MergeCandidate[];
  /** Aanbevolen master = oudste CreatedDate (meeste historie). */
  recommendedMasterId: string;
}

export type MergeCustomerResult =
  | { ok: true; masterId: string }
  | { ok: false; error: string; code?: string };

// ─── HELPERS ────────────────────────────────────────────────────

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string) {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(url, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return response.json();
}

/**
 * Map van UI-veldnamen → SF field namen. De store krijgt patches in UI-termen
 * (street, postcode, shippingStreet, brandFlags: { kleda: true }, ...) en de
 * SF-laag verwacht de API-naamgeving. Brand-flags worden per flag gepatcht.
 */
function toSfPatch(patch: AccountPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.name !== undefined) out.Name = patch.name;
  if (patch.firstName !== undefined) out.FirstName = patch.firstName;
  if (patch.lastName !== undefined) out.LastName = patch.lastName;
  if (patch.email !== undefined) out.PersonEmail = patch.email;
  if (patch.phone !== undefined) out.Phone = patch.phone;
  if (patch.mobile !== undefined) out.PersonMobilePhone = patch.mobile;
  if (patch.street !== undefined) out.BillingStreet = patch.street;
  if (patch.postcode !== undefined) out.BillingPostalCode = patch.postcode;
  if (patch.city !== undefined) out.BillingCity = patch.city;
  if (patch.country !== undefined) out.BillingCountry = patch.country;
  if (patch.billingAddressToevoeging !== undefined) out.BillingAddress_toevoeging__c = patch.billingAddressToevoeging;
  if (patch.shippingStreet !== undefined) out.ShippingStreet = patch.shippingStreet;
  if (patch.shippingPostcode !== undefined) out.ShippingPostalCode = patch.shippingPostcode;
  if (patch.shippingCity !== undefined) out.ShippingCity = patch.shippingCity;
  if (patch.shippingCountry !== undefined) out.ShippingCountry = patch.shippingCountry;
  if (patch.shippingAddressToevoeging !== undefined) out.ShippingAddress_toevoeging__c = patch.shippingAddressToevoeging;
  // Eén vinkje in de UI, in het domein de SF-picklist: aan = 'Factuur', uit = standaard (null).
  if (patch.betaaltOpFactuur !== undefined) out.Betalingswijze__c = patch.betaaltOpFactuur ? 'Factuur' : null;
  if (patch.brandFlags) {
    if (patch.brandFlags.cinco !== undefined) out.Cinco_Cleaning_Klant__c = patch.brandFlags.cinco;
    if (patch.brandFlags.kleda !== undefined) out.Kleda_Klant__c = patch.brandFlags.kleda;
    if (patch.brandFlags.sofasaver !== undefined) out.SofaSaver_Klant__c = patch.brandFlags.sofasaver;
    if (patch.brandFlags.schoonhuisshop !== undefined) out.Schoonhuisshop_nl_Klant__c = patch.brandFlags.schoonhuisshop;
  }
  return out;
}

// ─── STORE ──────────────────────────────────────────────────────

interface KlantenState {
  // Search
  searchQuery: string;
  searchResults: CustomerResult[];
  isSearching: boolean;

  // Detail
  activeCustomer: CustomerDetail | null;
  isLoadingDetail: boolean;

  // Actions
  setSearchQuery: (q: string) => void;
  searchCustomers: (query: string) => Promise<void>;
  fetchCustomerDetail: (id: string, type: 'account' | 'lead') => Promise<void>;
  clearSearch: () => void;
  clearDetail: () => void;

  updateCustomerAccount: (
    patch: AccountPatch
  ) => Promise<{ ok: true; opFactuurOrders?: OpFactuurOrdersUitkomst } | { ok: false; error: string }>;

  /**
   * Zet de marketing-opt-out op een Lead, PersonAccount of Contactpersoon.
   * Optimistic update op `customer.emailOptOut` (lead/account) of het juiste
   * contact in `contacts` (contact), met rollback bij fout.
   */
  setEmailConsent: (
    recordType: 'lead' | 'account' | 'contact',
    recordId: string,
    optOut: boolean,
    reden?: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;

  createCustomer: (input: CreateCustomerInput) => Promise<CreateCustomerResult>;

  /**
   * Voeg een Contact toe aan een bestaand Business Account. Caller draait
   * `fetchCustomerDetail` zelf na success — geen optimistic state-mutatie hier
   * omdat `activeCustomer.contacts` uit een aparte query komt.
   */
  createContact: (
    accountId: string,
    fields: ContactCreateFields
  ) => Promise<CreateContactResult>;

  updateContact: (
    contactId: string,
    patch: Partial<ContactCreateFields>
  ) => Promise<UpdateContactResult>;

  deleteContact: (contactId: string) => Promise<DeleteContactResult>;

  /**
   * Zet de actieve Account op de blacklist (geen zaken meer doen) of haalt 'm
   * eraf. Admin-only — de server handhaaft dit via requireRole(['admin']).
   * Optimistic update op customer.blacklisted/blacklistReden met rollback bij fout.
   */
  setBlacklist: (
    blacklist: boolean,
    reden: string
  ) => Promise<{ ok: true } | { ok: false; error: string }>;

  /**
   * Vind mogelijke duplicaat-accounts van het gegeven account (zelfde e-mail/
   * telefoon, gelijk type) met child-counts en een aanbevolen master. Bekijken
   * mag elke medewerker; de merge zelf is admin-only (server handhaaft).
   */
  fetchMergeCandidates: (accountId: string) => Promise<MergeCandidatesResult | null>;

  /**
   * Voeg een duplicaat-account samen in een master. Admin-only — de server
   * handhaaft requireRole(['admin']). Geen optimistic mutatie: de huidige
   * activeCustomer kan verdwijnen, dus de view herlaadt/navigeert na success.
   */
  mergeCustomer: (
    masterId: string,
    duplicateId: string,
    fieldOverrides?: Record<string, unknown>
  ) => Promise<MergeCustomerResult>;
}

export type UpdateContactResult =
  | { ok: true }
  | { ok: false; error: string; code?: string };

export interface DeleteContactReferences {
  werkOrders: number;
  invoices: number;
  geplandeRitten: number;
  leads: number;
  opportunities: number;
}

export type DeleteContactResult =
  | { ok: true }
  | { ok: false; error: string; code?: string; references?: DeleteContactReferences };

export interface ContactCreateFields {
  FirstName?: string;
  LastName: string;
  Email?: string;
  Phone?: string;
  MobilePhone?: string;
  Title?: string;
  Department?: string;
}

export type CreateContactResult =
  | { ok: true; contactId: string }
  | { ok: false; error: string; code?: string; existingContactId?: string };

export const useKlantenStore = create<KlantenState>()((set, get) => ({
  searchQuery: '',
  searchResults: [],
  isSearching: false,

  activeCustomer: null,
  isLoadingDetail: false,

  setSearchQuery: (q: string) => set({ searchQuery: q }),

  searchCustomers: async (query: string) => {
    if (!query || query.length < 2) {
      set({ searchResults: [], isSearching: false });
      return;
    }
    set({ isSearching: true });
    try {
      const data = await apiFetch(`/api/customers/search?q=${encodeURIComponent(query)}`);
      set({ searchResults: data.results || [], isSearching: false });
    } catch (error) {
      console.error('[Klanten] search error:', error);
      set({ searchResults: [], isSearching: false });
    }
  },

  fetchCustomerDetail: async (id: string, type: 'account' | 'lead') => {
    set({ isLoadingDetail: true });
    try {
      const data: CustomerDetail = await apiFetch(`/api/customers/detail?id=${encodeURIComponent(id)}&type=${type}`);
      set({ activeCustomer: data, isLoadingDetail: false });

      // Voed recent-bezocht (lokaal per device). type-detail retourneert
      // 'account' of 'lead'; voor PersonAccount-vs-Business hebben we
      // isPersonAccount nodig om de juiste card-stijl te kiezen.
      const c = data.customer;
      const recentType: 'account_person' | 'account_business' | 'lead' =
        c.type === 'lead'
          ? 'lead'
          : c.isPersonAccount === false
            ? 'account_business'
            : 'account_person';
      useRecentCustomersStore.getState().push({
        id: c.id,
        type: recentType,
        name: c.name,
        email: c.email,
        phone: c.phone,
        city: c.city,
        postcode: c.postcode,
        brandFlags: c.type === 'account' ? c.brandFlags : undefined,
      });
    } catch (error) {
      console.error('[Klanten] detail error:', error);
      set({ isLoadingDetail: false });
    }
  },

  clearSearch: () => set({ searchQuery: '', searchResults: [] }),

  clearDetail: () => set({ activeCustomer: null }),

  updateCustomerAccount: async (patch) => {
    const current = get().activeCustomer;
    if (!current) return { ok: false, error: 'Geen actieve klant' };
    if (current.customer.type !== 'account') {
      return { ok: false, error: 'Alleen accounts zijn bewerkbaar' };
    }

    const sfPatch = toSfPatch(patch);
    if (Object.keys(sfPatch).length === 0) {
      return { ok: false, error: 'Geen wijzigingen' };
    }

    // Snapshot voor rollback
    const snapshot = current.customer;

    // Optimistic update
    const optimistic: CustomerCore = {
      ...snapshot,
      ...patch,
      brandFlags: {
        ...snapshot.brandFlags,
        ...(patch.brandFlags ?? {}),
      },
    };
    set({ activeCustomer: { ...current, customer: optimistic } });

    try {
      const token = await getToken();
      const res = await fetch('/api/customers/account-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ accountId: snapshot.id, patch: sfPatch }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Update mislukt' }));
        // Rollback
        set({ activeCustomer: { ...current, customer: snapshot } });
        return { ok: false, error: err.error || 'Update mislukt' };
      }

      const data = await res.json();
      // Sync lastModifiedDate van server; andere velden zijn al optimistisch gezet
      set({
        activeCustomer: {
          ...get().activeCustomer!,
          customer: {
            ...optimistic,
            lastModifiedDate: data.nieuweLastModifiedDate ?? optimistic.lastModifiedDate,
            mutatieBron: 'Office-App',
            ...(patch.betaaltOpFactuur !== undefined ? { betalingswijze: patch.betaaltOpFactuur ? 'Factuur' : null } : {}),
          },
        },
      });
      return { ok: true, ...(data.opFactuurOrders ? { opFactuurOrders: data.opFactuurOrders as OpFactuurOrdersUitkomst } : {}) };
    } catch (err) {
      set({ activeCustomer: { ...current, customer: snapshot } });
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  setEmailConsent: async (recordType, recordId, optOut, reden) => {
    const current = get().activeCustomer;
    if (!current) return { ok: false, error: 'Geen actieve klant' };

    // Optimistic: customer-level (lead/account) of een specifiek contact.
    const applyOptOut = (detail: CustomerDetail, value: boolean): CustomerDetail => {
      if (recordType === 'contact') {
        return {
          ...detail,
          contacts: (detail.contacts || []).map((c) =>
            c.id === recordId ? { ...c, emailOptOut: value } : c
          ),
        };
      }
      return { ...detail, customer: { ...detail.customer, emailOptOut: value } };
    };

    set({ activeCustomer: applyOptOut(current, optOut) });

    try {
      const token = await getToken();
      const res = await fetch('/api/customers/email-consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ recordType, recordId, optOut, reden: reden ?? '' }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Opt-out bijwerken mislukt' }));
        set({ activeCustomer: current }); // rollback
        return { ok: false, error: err.error || 'Opt-out bijwerken mislukt' };
      }
      return { ok: true };
    } catch (err) {
      set({ activeCustomer: current }); // rollback
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  createCustomer: async (input) => {
    const account: Record<string, unknown> = {};
    let contact: Record<string, unknown> | undefined;

    const setIf = (target: Record<string, unknown>, key: string, value: unknown) => {
      if (value === undefined || value === null) return;
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (trimmed === '') return;
      target[key] = trimmed;
    };

    if (input.type === 'Person') {
      setIf(account, 'FirstName', input.firstName);
      setIf(account, 'LastName', input.lastName);
      setIf(account, 'PersonEmail', input.email);
      setIf(account, 'Phone', input.phone);
      setIf(account, 'PersonMobilePhone', input.mobile);
      setIf(account, 'BillingStreet', input.street);
      setIf(account, 'BillingPostalCode', input.postcode);
      setIf(account, 'BillingCity', input.city);
      setIf(account, 'BillingCountry', input.country);
    } else {
      setIf(account, 'Name', input.companyName);
      setIf(account, 'Phone', input.accountPhone);
      setIf(account, 'BillingStreet', input.street);
      setIf(account, 'BillingPostalCode', input.postcode);
      setIf(account, 'BillingCity', input.city);
      setIf(account, 'BillingCountry', input.country);

      contact = {};
      setIf(contact, 'FirstName', input.firstName);
      setIf(contact, 'LastName', input.lastName);
      setIf(contact, 'Email', input.email);
      setIf(contact, 'Phone', input.phone);
      setIf(contact, 'MobilePhone', input.mobile);
    }

    try {
      const token = await getToken();
      const res = await fetch('/api/customers/account-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: input.type,
          account,
          contact,
          skipDuplicateCheck: input.skipDuplicateCheck === true,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          error: data?.error || 'Aanmaken mislukt',
          code: data?.code,
          duplicates: data?.duplicates as DuplicateCustomerMatch[] | undefined,
        };
      }
      return {
        ok: true,
        accountId: data.accountId,
        contactId: data.contactId ?? null,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  createContact: async (accountId, fields) => {
    try {
      const token = await getToken();
      const res = await fetch('/api/customers/contact-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ accountId, fields }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          error: data?.error || 'Aanmaken mislukt',
          code: data?.code,
          existingContactId: data?.existingContactId ?? undefined,
        };
      }
      return { ok: true, contactId: data.contactId };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  updateContact: async (contactId, patch) => {
    try {
      const token = await getToken();
      const res = await fetch('/api/customers/contact-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contactId, patch }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { ok: false, error: data?.error || 'Bijwerken mislukt', code: data?.code };
      }
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  deleteContact: async (contactId) => {
    try {
      const token = await getToken();
      const res = await fetch('/api/customers/contact-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ contactId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return {
          ok: false,
          error: data?.error || 'Verwijderen mislukt',
          code: data?.code,
          references: data?.references as DeleteContactReferences | undefined,
        };
      }
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  setBlacklist: async (blacklist, reden) => {
    const current = get().activeCustomer;
    if (!current) return { ok: false, error: 'Geen actieve klant' };
    if (current.customer.type !== 'account') {
      return { ok: false, error: 'Alleen accounts kunnen op de blacklist' };
    }

    const snapshot = current.customer;
    const trimmedReden = reden.trim();

    // Optimistic update — banner reageert direct.
    const optimistic: CustomerCore = {
      ...snapshot,
      blacklisted: blacklist,
      blacklistReden: blacklist ? (trimmedReden || null) : null,
    };
    set({ activeCustomer: { ...current, customer: optimistic } });

    try {
      const token = await getToken();
      const res = await fetch('/api/customers/blacklist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ accountId: snapshot.id, blacklist, reden: trimmedReden }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Blacklist bijwerken mislukt' }));
        set({ activeCustomer: { ...current, customer: snapshot } }); // rollback
        return { ok: false, error: err.error || 'Blacklist bijwerken mislukt' };
      }

      const data = await res.json();
      set({
        activeCustomer: {
          ...get().activeCustomer!,
          customer: {
            ...optimistic,
            lastModifiedDate: data.nieuweLastModifiedDate ?? optimistic.lastModifiedDate,
            mutatieBron: 'Office-App',
          },
        },
      });
      return { ok: true };
    } catch (err) {
      set({ activeCustomer: { ...current, customer: snapshot } }); // rollback
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },

  fetchMergeCandidates: async (accountId) => {
    try {
      return await apiFetch(`/api/customers/merge-candidates?accountId=${encodeURIComponent(accountId)}`);
    } catch (error) {
      console.error('[Klanten] merge-candidates error:', error);
      return null;
    }
  },

  mergeCustomer: async (masterId, duplicateId, fieldOverrides) => {
    try {
      const token = await getToken();
      const res = await fetch('/api/customers/merge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ masterId, duplicateId, fieldOverrides: fieldOverrides ?? {} }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { ok: false, error: data?.error || 'Samenvoegen mislukt', code: data?.code };
      }
      return { ok: true, masterId: data.masterId ?? masterId };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netwerkfout';
      return { ok: false, error: message };
    }
  },
}));
